<template>
  <div class="w-full bg-slate-50 min-h-screen p-4" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-7xl mx-auto space-y-8">
      
      <!-- HEADER -->
      <div class="mb-6">
        <h1 class="text-2xl font-bold text-slate-700">Gestão de Veículos</h1>
        <p class="text-sm text-slate-500">Administração e controle da frota municipal</p>
      </div>

      <!-- ÁREA DE PESQUISA -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-custom mb-8">
          <div class="bg-slate-700 px-6 py-4">
              <h2 class="text-white font-bold flex items-center gap-2" style="font-size: 1rem;">
                  <v-icon color="white" small class="mr-2">mdi-magnify</v-icon>
                  PESQUISAR
              </h2>
          </div>
          <div class="px-6 pt-6 pb-10">
              <div class="flex items-end gap-4">
                  <div class="flex-1 w-full">
                      <label class="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" style="margin-bottom: 8px; display: block;">Prefeitura</label>
                      <div class="relative">
                          <v-autocomplete
                            v-model="filter.prefecture_id"
                            :items="prefectures"
                            item-text="prefecture_social_name"
                            item-value="prefecture_id"
                            placeholder="Selecione uma opção..."
                            solo
                            flat
                            dense
                            hide-details
                            class="custom-select"
                            height="48"
                            :readonly="!isAdmin"
                          ></v-autocomplete>
                      </div>
                  </div>
                  <div class="flex-none">
                      <button @click="loadVehicles" class="bg-orange-500 hover:bg-orange-600 text-white rounded-lg shadow-md shadow-orange-500/10 flex items-center justify-center gap-2 w-fit whitespace-nowrap px-8 h-12 transition-all transform hover:-translate-y-0.5">
                          <v-icon color="white" small class="mr-1" style="font-size: 14px;">mdi-filter-outline</v-icon>
                          <span class="text-[11px] font-black uppercase">FILTRAR</span>
                      </button>
                  </div>
              </div>
          </div>
      </section>

      <!-- GRID DE VEÍCULOS -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-custom">
          <!-- Toolbar do Grid -->
          <div class="bg-slate-700 px-4 py-4 flex items-center w-full">
              <div class="flex items-center gap-2 flex-none">
                <v-icon color="white" small>mdi-car</v-icon>
                <h2 class="text-white font-semibold uppercase tracking-wider" style="font-size: 1rem;">VEÍCULOS</h2>
              </div>
              
              <div class="flex items-center gap-2 flex-1 justify-end">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <button v-bind="attrs" v-on="on" @click="printQrCodes" class="p-2 rounded-lg text-custom-gray hover:bg-slate-600 hover:text-white transition-colors">
                        <v-icon color="currentColor">mdi-qrcode</v-icon>
                      </button>
                    </template>
                    <span>Imprimir QR Codes</span>
                  </v-tooltip>

                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <button v-bind="attrs" v-on="on" @click="printPasswords" class="p-2 rounded-lg text-custom-gray hover:bg-slate-600 hover:text-white transition-colors">
                        <v-icon color="currentColor">mdi-printer</v-icon>
                      </button>
                    </template>
                    <span>Imprimir Senhas</span>
                  </v-tooltip>

                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <button v-bind="attrs" v-on="on" @click="loadVehicles" class="p-2 rounded-lg text-custom-gray hover:bg-slate-600 hover:text-white transition-colors">
                        <v-icon color="currentColor">mdi-history</v-icon>
                      </button>
                    </template>
                    <span>Histórico</span>
                  </v-tooltip>

                  <div class="h-6 border-r border-slate-500 mx-2"></div>
                  
                  <div class="flex-none">
                    <button 
                      v-if="isAdmin || isPrefecture" 
                      @click="$router.push('/vehicles-manager/create')" 
                      class="bg-orange-500 hover:bg-orange-600 text-white shadow-lg flex items-center justify-center gap-2 rounded-lg px-6 py-2 transition-all w-fit max-w-max whitespace-nowrap"
                      style="width: auto !important; flex: none !important;"
                    >
                      <v-icon color="white" small>mdi-plus</v-icon>
                      <span class="font-bold text-sm">NOVO</span>
                    </button>
                  </div>

                  <button @click="showSearch = !showSearch" class="p-2 rounded-lg text-custom-gray hover:bg-slate-600 hover:text-white transition-colors">
                     <v-icon color="currentColor">mdi-magnify</v-icon>
                  </button>
              </div>
          </div>

          <!-- Inline Search -->
           <v-expand-transition>
            <div v-if="showSearch" class="bg-slate-50 px-6 py-4 border-b border-slate-200 shadow-inner">
              <div class="flex items-center w-full px-3 py-2 bg-white border border-slate-300 rounded-lg shadow-sm focus-within:ring-1 focus-within:ring-orange-500 focus-within:border-orange-500 transition-all">
                  <v-icon color="#94a3b8" small class="mr-2">mdi-magnify</v-icon>
                  <input 
                    v-model="search" 
                    type="text" 
                    placeholder="Pesquisar por placa, modelo..." 
                    class="w-full text-sm text-slate-700 placeholder-slate-400 focus:outline-none bg-transparent border-none p-0"
                  >
              </div>
            </div>
           </v-expand-transition>

          <!-- Tabela -->
          <div class="overflow-x-auto">
             <div class="min-w-[1000px]">
               <v-data-table
                :headers="headers"
                :items="data"
                :search="search"
                :loading="loader"
                hide-default-footer
                class="custom-table"
                :page.sync="page"
                :items-per-page="itemsPerPage"
                @page-count="pageCount = $event"
              >
                <!-- ID -->
                <template v-slot:item.vehicle_id="{ item }">
                  <span class="text-sm font-medium text-slate-400">#{{ item.vehicle_id }}</span>
                </template>

                <!-- Secretaria -->
                <template v-slot:item.sec_name="{ item }">
                    <div class="flex flex-column py-2">
                      <span class="text-sm font-bold text-slate-700">{{ item.sec_name }}</span>
                      <span class="text-xs text-slate-400 mt-1">{{ item.subsec_desc || 'Geral' }}</span>
                    </div>
                </template>

                <!-- Modelo -->
                <template v-slot:item.model_desc="{ item }">
                    <div class="flex flex-column py-2">
                      <span class="text-sm font-bold text-slate-700 uppercase">{{ item.model_desc }}</span>
                      <span class="mt-1 bg-slate-100 text-slate-600 text-xs font-bold px-2 py-1 rounded border border-slate-200 uppercase w-fit-content">
                          {{ item.vehicle_board }}
                      </span>
                    </div>
                </template>

                <!-- Combustivel -->
                <template v-slot:item.fuel_desc="{ item }">
                   <span class="text-xs font-medium text-slate-600 uppercase">{{ item.fuel_desc }}</span>
                </template>

                <!-- Status -->
                <template v-slot:item.vehicle_active="{ item }">
                   <span :class="item.vehicle_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'" class="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider">
                        {{ item.vehicle_active ? "SIM" : "NÃO" }}
                    </span>
                </template>

                <!-- Relatorio -->
                <template v-slot:item.vehicle_reports="{ item }">
                   <span :class="item.vehicle_reports ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-400'" class="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider">
                        {{ item.vehicle_reports ? "SIM" : "NÃO" }}
                    </span>
                </template>

                <!-- Senha -->
                <template v-slot:item.vehicle_password="{ item }">
                    <span class="text-xs font-bold font-mono text-orange-600 bg-orange-50 px-2 py-1 rounded border border-orange-100">
                      {{ item.vehicle_password }}
                    </span>
                </template>

                <!-- Ações -->
                <template v-slot:item.action="{ item }">
                   <v-menu offset-y left>
                      <template v-slot:activator="{ on, attrs }">
                        <button class="btn-icon-slate" v-bind="attrs" v-on="on">
                            <v-icon small color="#64748b">mdi-dots-vertical</v-icon>
                        </button>
                      </template>
                       <v-list dense rounded>
                          <v-list-item @click="addFuelAuthorization(item)" link>
                            <v-list-item-icon><v-icon color="orange" small class="mr-2">mdi-gas-station</v-icon></v-list-item-icon>
                            <v-list-item-title>Autorizar Abastecimento</v-list-item-title>
                          </v-list-item>
                          
                          <v-list-item @click="loadAbastecimento(item)" link>
                            <v-list-item-icon><v-icon color="blue" small class="mr-2">mdi-history</v-icon></v-list-item-icon>
                            <v-list-item-title>Histórico</v-list-item-title>
                          </v-list-item>

                          <v-list-item @click="printQrCode(item)" link>
                             <v-list-item-icon><v-icon color="teal" small class="mr-2">mdi-qrcode</v-icon></v-list-item-icon>
                             <v-list-item-title>Imprimir QR Code</v-list-item-title>
                          </v-list-item>

                          <v-list-item @click="resetPassword(item.vehicle_id)" link>
                             <v-list-item-icon><v-icon color="grey" small class="mr-2">mdi-lock-reset</v-icon></v-list-item-icon>
                             <v-list-item-title>Resetar Senha</v-list-item-title>
                          </v-list-item>
                          <v-list-item v-if="isAdmin || isPrefecture" @click="$router.push(`/vehicles-manager/edit/${item.vehicle_id}`)" link>
                             <v-list-item-icon><v-icon color="blue" small class="mr-2">mdi-pencil</v-icon></v-list-item-icon>
                             <v-list-item-title>Editar</v-list-item-title>
                          </v-list-item>
                          <v-list-item @click="deleteItem(item.vehicle_id)">
                             <v-list-item-icon><v-icon color="red" small>mdi-delete</v-icon></v-list-item-icon>
                             <v-list-item-title class="red--text">Excluir</v-list-item-title>
                          </v-list-item>
                       </v-list>
                   </v-menu>
                </template>
               </v-data-table>

               <!-- Custom Pagination Footer -->
               <div class="bg-slate-50 px-6 py-4 border-t border-slate-200 flex flex-column md:flex-row justify-between items-center gap-4 text-xs text-slate-500 font-medium w-full">
                   <span>Exibindo {{ paginationStart }} de {{ data.length }} registros</span>
                   
                   <div class="flex gap-1">
                       <button 
                         @click="page > 1 ? page-- : null" 
                         :disabled="page === 1"
                         class="px-3 py-1.5 bg-white border border-slate-200 rounded-md hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                       >
                         Anterior
                       </button>
                       
                       <button 
                         v-for="p in visiblePages" 
                         :key="p"
                         @click="page = p"
                         :class="p === page ? 'bg-slate-700 text-white border-slate-700 shadow-sm' : 'bg-white border-slate-200 hover:bg-slate-100'"
                         class="px-3 py-1.5 border rounded-md transition-colors font-medium"
                       >
                         {{ p }}
                       </button>

                       <button 
                         @click="page < pageCount ? page++ : null" 
                         :disabled="page === pageCount"
                         class="px-3 py-1.5 bg-white border border-slate-200 rounded-md hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                       >
                         Próximo
                       </button>
                   </div>
               </div>
             </div>
          </div>
      </section>
    </div>

    <!-- Hidden Components -->
    <div style="display: none">
        <passCard :data="printData" />
        <passCardList :data="data" />
        <passQrCode :data="printDataQrCode" />
        <passQrCodeList :data="data" />
    </div>

    <!-- Modals (Simplified for brevity in this view, logic persists) -->
    <v-dialog v-model="showModal" max-width="800px">
       <v-card class="rounded-xl overflow-hidden">
         <div class="bg-slate-700 px-6 py-4 flex justify-between items-center text-white">
           <span class="font-bold">{{ modalTitle }}</span>
           <v-icon color="white" @click="showModal = false">mdi-close</v-icon>
         </div>
         <v-card-text class="pt-6">
            <!-- Form logic here (same as before) -->
            <v-form ref="formMotorista" v-model="validForm" lazy-validation>
              <v-container>
                 <v-row dense>
                    <v-col cols="12" md="6"><v-autocomplete label="Fornecedor" :items="providers" v-model="dataForm.provider_id" outlined dense item-text="provider_name" item-value="provider_id" @change="loadItems(dataForm.provider_id, dataForm.vehicle_id, dataForm.prefecture_id)" /></v-col>
                    <v-col cols="12" md="6"><v-autocomplete label="Item" :items="items" v-model="dataForm.item_id" outlined dense item-text="item_description_desc" item-value="item_id"></v-autocomplete></v-col>
                    <v-col cols="12"><v-autocomplete label="Motorista" :items="drivers" v-model="dataForm.driver_id" outlined dense item-text="driver_name" item-value="driver_id" append-icon="mdi-plus" @click:append="showFormMotorista = true" /></v-col>
                    <v-col cols="12" md="6"><v-text-field label="Litros" v-model="dataForm.supply_authorized_liters" outlined dense v-mask-decimal.br="3" /></v-col>
                     <v-col cols="12" md="6"><v-text-field label="Valor (R$)" v-model="dataForm.supply_authorized_value" outlined dense v-mask-decimal.br="2" /></v-col>
                 </v-row>
              </v-container>
            </v-form>
         </v-card-text>
         <div class="px-6 pb-6 flex justify-end gap-2">
            <button class="px-4 py-2 text-slate-500 font-bold uppercase text-sm" @click="showModal = false">Cancelar</button>
            <button class="px-6 py-2 bg-emerald-500 text-white font-bold uppercase text-sm rounded-lg hover:bg-emerald-600" @click="createSupplyAuthorization(dataForm)">Confirmar</button>
         </div>
       </v-card>
    </v-dialog>

    <v-dialog v-model="showFormMotorista" width="400">
       <v-card class="rounded-xl">
          <div class="bg-slate-700 px-6 py-3 text-white font-bold">Novo Motorista</div>
          <v-card-text class="pt-6">
             <v-text-field label="Nome" v-model="driver_name" outlined dense></v-text-field>
          </v-card-text>
          <div class="px-6 pb-4 flex justify-end gap-2">
             <button class="px-4 py-2 text-red-500 font-bold uppercase text-sm" @click="showFormMotorista = false">Cancelar</button>
             <button class="px-4 py-2 bg-slate-700 text-white font-bold uppercase text-sm rounded-lg" @click="newDriver">Salvar</button>
          </div>
       </v-card>
    </v-dialog>

     <v-dialog v-model="showModalAbastecimento" max-width="900">
       <v-card class="rounded-xl">
          <div class="bg-slate-700 px-6 py-3 text-white font-bold flex justify-center">{{ modalTitleAbastecimento }}</div>
          <v-card-text class="pa-0">
             <v-data-table :headers="headersAbastecimento" :items="abastecimentoData" :loading="loaderAbastecimento" class="elevation-0">
                <template v-slot:item.supply_authorized_value="{ item }">
                   <span class="text-emerald-600 font-bold">R$ {{ realMask(item.supply_authorized_value.toFixed(2)) }}</span>
                </template>
                <template v-slot:item.datehour="{ item }"><span>{{ convertDateTime(item.datehour) }}</span></template>
             </v-data-table>
          </v-card-text>
       </v-card>
    </v-dialog>

    <v-snackbar v-model="snack.show" :color="snack.color" top right>{{ snack.message }}</v-snackbar>
  </div>
</template>

<script>
import passCard from "./passCard";
import passCardList from "./listPassCards";
import passQrCode from "./passQrCode";
import passQrCodeList from "./listPassQrCodes";
import Helpers from "@/mixins/helpers.js";
export default {
  props: ["id"],
  components: {
    passCard,
    passCardList,
    passQrCode,
    passQrCodeList,
  },
  mixins: [Helpers],
  data() {
    return {
      search: "",
      loader: true,
      loaderAbastecimento: false,
      showModalAbastecimento: false,
      modalTitleAbastecimento: "Autorizações de Abastecimento",
      abastecimentoData: {},
      showSearch: false,
      data: [],
      printData: [],
      printDataQrCode: [],
      prefectures: [],
      filter: {
        prefecture_id: null,
      },
      snack: {
        show: false,
        message: "",
        color: "",
      },
      headers: [
        { text: "ID", value: "vehicle_id", sortable: true, width: "80px", class: "text-uppercase caption font-weight-bold" },
        { text: "SECRETARIA / SUB-SETOR", value: "sec_name", sortable: true, class: "text-uppercase caption font-weight-bold" },
        { text: "MODELO / PLACA", value: "model_desc", sortable: true, class: "text-uppercase caption font-weight-bold" },
        { text: "COMBUSTÍVEL", value: "fuel_desc", sortable: true, class: "text-uppercase caption font-weight-bold" },
        { text: "STATUS", value: "vehicle_active", sortable: true, align: "center", class: "text-uppercase caption font-weight-bold" },
        { text: "RELATÓRIO", value: "vehicle_reports", sortable: true, align: "center", class: "text-uppercase caption font-weight-bold" },
        { text: "SENHA", value: "vehicle_password", sortable: true, align: "center", class: "text-uppercase caption font-weight-bold" },
        { text: "AÇÕES", align: "right", value: "action", sortable: false, class: "text-uppercase caption font-weight-bold" },
      ],
      headersAbastecimento: [
        { text: "Data e Hora", value: "datehour", align: "center" },
        { text: "Motorista", value: "driver_name" },
        { text: "Fornecedor", value: "provider_name", class: "d-none d-sm-table-cell", cellClass: "d-none d-sm-table-cell" },
        { text: "Item", value: "item_description_desc", class: "d-none d-md-table-cell", cellClass: "d-none d-md-table-cell" },
        { text: "Litros Autorizados", value: "supply_authorized_liters", align: "center" },
        { text: "Valor Autorizado", value: "supply_authorized_value", align: "center", class: "d-none d-sm-table-cell", cellClass: "d-none d-sm-table-cell" },
        { text: "Status", value: "supply_status", align: "center" }
      ],
      showModal: false,
      showFormMotorista: false,
      loadingModal: false,
      loadingMotorista: false,
      loadingSubmit: false,
      validForm: false,
      providers: [],
      drivers: [],
      items: [],
      driver_name: "",
      fieldRules: [(v) => !!v || "Campo obrigatório"],
      modalTitle: "Autorizar Abastecimento",
      dataForm: {
        supply_authorized_liters: "0",
        supply_authorized_value: "0",
        supply_status: "PRE-AUTORIZADO",
        supply_adjusted: false,
        supply_extra: false,
        supply_liters_stocked: false,
        supply_billed: false,
        vehicle_id: null,
        driver_id: null,
        item_id: null,
        prefecture_id: null,
        provider_id: null,
        is_autorized: true,
        authorizer: this.$store.getters.getUserData.user_login,
      },
      page: 1,
      pageCount: 0,
      itemsPerPage: 10,
    };
  },
  created() {
    this.initialize();
  },
  computed: {
    disableLitros() {
      return parseFloat(this.dataForm.supply_authorized_value.replace(',', '.')) > 0;
    },
    disableValor() {
      return parseFloat(this.dataForm.supply_authorized_liters.replace(',', '.')) > 0;
    },
    isAdmin() {
      return this.$store.getters.getUserData.group_id === 1;
    },
    isPrefecture() {
      return this.$store.getters.getUserData.table_key === 'prefectures';
    },
    paginationStart() {
      return (this.page - 1) * this.itemsPerPage + 1;
    },
    visiblePages() {
      const pages = [];
      const totalVisible = 5;
      
      let start = Math.max(1, this.page - Math.floor(totalVisible / 2));
      let end = Math.min(this.pageCount, start + totalVisible - 1);

      if (end - start + 1 < totalVisible) {
        start = Math.max(1, end - totalVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    }
  },
  methods: {
    async addFuelAuthorization(item) {
      this.clearForm();
      this.modalTitle = `Autorizar Abastecimento - Veículo: ${item.vehicle_board}`;
      this.showModal = true;
      this.loadingModal = true;
      
      const driverRequest = this.$http.get(`/supplies/drivers/${item.prefecture_id}`);
      const providerRequest = this.$http.get(`/providers/prefecture/${item.prefecture_id}`);

      const [driversResponse, providersResponse] = await Promise.all([driverRequest, providerRequest]);

      this.drivers = driversResponse.data;
      this.providers = providersResponse.data;

      this.dataForm.vehicle_id = item.vehicle_id;
      this.dataForm.prefecture_id = item.prefecture_id;
      this.dataForm.provider_id = this.providers.length > 0 ? this.providers[0].provider_id : null;

      if (this.dataForm.provider_id) {
        await this.loadItems(this.dataForm.provider_id, item.vehicle_id, item.prefecture_id);
      }

      this.loadingModal = false;
    },
    async loadItems(provider_id, vehicle_id, prefecture_id) {
      const res = await this.$http.get(`/supplies/providers/${provider_id}/${vehicle_id}/${prefecture_id}`);
      this.items = res.data;
      this.dataForm.item_id = this.items.length > 0 ? this.items[0].item_id : null;
    },
    clearForm() {
      this.dataForm = {
        supply_authorized_liters: "0",
        supply_authorized_value: "0",
        supply_status: "PRE-AUTORIZADO",
        supply_adjusted: false,
        supply_extra: false,
        supply_liters_stocked: false,
        supply_billed: false,
        vehicle_id: null,
        driver_id: null,
        item_id: null,
        prefecture_id: null,
        provider_id: null,
        is_autorized: true,
        authorizer: this.$store.getters.getUserData.user_login,
      };
      this.providers = [];
      this.items = [];
      this.drivers = [];
    },
    newDriver() {
      this.loadingMotorista = true;
      this.$http
        .post("/supplies/drivers", {
          driver_name: this.driver_name,
          prefecture_id: this.dataForm.prefecture_id,
        })
        .then((r) => {
          this.snack.show = true;
          this.snack.message = r.data.result;
          this.snack.color = "success";
          this.$http.get(`/supplies/drivers/${this.dataForm.prefecture_id}`).then((d) => {
            this.drivers = d.data;
            const driverItems = d.data.filter((f) => f.driver_name === this.driver_name ? f : null);
            this.dataForm.driver_id = driverItems[0].driver_id;
            this.loadingMotorista = false;
            this.showFormMotorista = false;
            this.driver_name = '';
          });
        })
        .catch((e) => {
          this.snack.show = true;
          this.snack.color = "error";
          this.snack.message = e.message;
          this.loadingMotorista = false;
        });
    },
    async createSupplyAuthorization(data) {
      if (this.$refs.formMotorista.validate()) {
        try {
          this.loadingSubmit = true;
          await this.$http.post("/supplies/single", data);
          this.snack.show = true;
          this.snack.message = "Autorização de abastecimento criada com sucesso!";
          this.snack.color = "success";
          this.loadingSubmit = false;
          this.showModal = false;
          this.clearForm();
        } catch (error) {
          this.snack.show = true;
          this.snack.color = "error";
          this.snack.message = error.message;
          this.loadingSubmit = false;
        }
      }
    },
    printPasswords() {
      this.$htmlToPaper("printCards");
    },
    printCard(d) {
      this.printData = d;
      setTimeout(() => this.$htmlToPaper("printMe"), 1000);
    },
    printQrCodes() {
      this.$htmlToPaper("printQrCodes");
    },
    printQrCode(d) {
      this.printDataQrCode = d;
      setTimeout(() => this.$htmlToPaper("printMeQrCode"), 1000);
    },
    initialize() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);

      this.$http.get("/prefectures").then((r) => {
        this.prefectures = r.data;
        this.loader = false;
        this.$store.dispatch("setLoader", this.loader);

        if (this.$store.getters.getUserData.table_key === 'prefectures' && this.$store.getters.getUserData.table_id) {
          this.filter.prefecture_id = this.$store.getters.getUserData.table_id;
          this.loadVehicles();
        } else if (this.$store.getters.getUserData.group_id !== 1) {
          this.filter.prefecture_id = this.$store.getters.getUserData.table_id;
          this.loadVehicles();
        } else if (this.id) {
          this.filter.prefecture_id = this.id;
          this.loadVehicles();
        }
      }).catch(() => {
        this.loader = false;
        this.$store.dispatch("setLoader", this.loader);
      });
    },
    loadVehicles() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.$http
        .get(`/vehicles/prefectures/${this.filter.prefecture_id}`)
        .then((r) => {
          if (this.isAdmin) {
            this.data = r.data;
          } else {
            this.data = r.data.filter(v => v.vehicle_active === true);
          }

          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
        })
        .catch((e) => {
          this.loader = false;
          this.snack.show = true;
          this.snack.message = e;
          this.snack.type = "error";
          this.$store.dispatch("setLoader", this.loader);
        });
    },
    deleteItem(id) {
      let c = confirm("Deseja excluir o registro?");
      if (c) {
        this.$store.dispatch("setLoader", true);
        this.$http
          .delete(`/vehicles/${id}`)
          .then((r) => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.snack.color = "success";
            this.loadVehicles();
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.$store.dispatch("setLoader", false);
          });
      }
    },
    resetPassword(id) {
      let c = confirm("Deseja resetar a senha do veículo?");
      if (c) {
        this.$store.dispatch("setLoader", true);
        this.$http
          .patch(`/vehicles/reset/password/${id}`)
          .then((r) => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.snack.color = "success";
            this.loadVehicles();
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.$store.dispatch("setLoader", false);
          });
      }
    },
    async loadAbastecimento(vehicle) {
      this.loaderAbastecimento = true;
      this.abastecimentoData = [];
      this.modalTitleAbastecimento = `Autorizações de Abastecimento - Veículo: ${vehicle.vehicle_board}`;
      this.showModalAbastecimento = true;

      const filter = {
        prefecture_id: vehicle.prefecture_id,
        vehicle_id: vehicle.vehicle_id,
        all_status: true,
        authorizer: this.$store.getters.getUserData.user_login,
        is_autorized: true,
      }

      const res = await this.$http.put("/supplies/billings", filter);
      this.abastecimentoData = res.data;

      this.loaderAbastecimento = false;
    },
  },
};
</script>

<style scoped>
/* Utility Classes mimicking Tailwind */
.min-h-screen { min-height: 100vh; }
.p-4 { padding: 1rem; }
.px-6 { padding-left: 1.5rem; padding-right: 1.5rem; }
.py-4 { padding-top: 1rem; padding-bottom: 1rem; }
.py-2 { padding-top: 0.5rem; padding-bottom: 0.5rem; }
.px-2 { padding-left: 0.5rem; padding-right: 0.5rem; }
.mb-6 { margin-bottom: 1.5rem; }
.mb-8 { margin-bottom: 2rem; }
.ml-auto { margin-left: auto; }
.gap-2 { gap: 0.5rem; }
.gap-4 { gap: 1rem; }
.w-full { width: 100%; }
.w-fit-content { width: fit-content; }
.min-w-\[1000px\] { min-width: 1000px; }
.h-6 { height: 1.5rem; }
.h-12 { height: 48px; }
.w-px { width: 1px; }

/* Flexbox */
.flex { display: flex; }
.flex-column { flex-direction: column; }
.items-center { align-items: center; }
.items-end { align-items: flex-end; }
.justify-between { justify-content: space-between; }
.justify-center { justify-content: center; }
.justify-end { justify-content: flex-end; }

@media (min-width: 768px) {
  .md\:flex-row { flex-direction: row; }
}

.flex-col { flex-direction: column; }

/* Colors */
.text-slate-700 { color: #334155; }
.text-slate-500 { color: #64748b; }
.text-slate-400 { color: #94a3b8; }
.text-slate-300 { color: #cbd5e1; }
.text-custom-gray { color: #9e9e9e !important; }
.text-white { color: #ffffff; }
.hover\:bg-slate-600:hover { background-color: #475569; }
.hover\:text-white:hover { color: #ffffff !important; }
.shadow-emerald { box-shadow: 0 10px 15px -3px rgba(6, 78, 59, 0.2); }

.bg-slate-50 { background-color: #f8fafc; }
.bg-slate-100 { background-color: #f1f5f9; }
.bg-slate-200 { background-color: #e2e8f0; }
.bg-slate-300 { background-color: #cbd5e1; }
.bg-slate-500 { background-color: #64748b; }
.bg-slate-600 { background-color: #475569; }
.bg-slate-700 { background-color: #333333; }
.bg-slate-800 { background-color: #1e293b; }
.bg-white { background-color: #ffffff; }
.bg-emerald-100 { background-color: #d1fae5; }
.bg-emerald-500 { background-color: #ea580c; }
.bg-emerald-600 { background-color: #059669; }
.bg-red-100 { background-color: #fee2e2; }
.bg-blue-100 { background-color: #dbeafe; }
.bg-orange-50 { background-color: #fff7ed; }
.border-orange-100 { border-color: #ffedd5; }
.bg-orange-500 { background-color: #f97316; }
.hover\:bg-orange-600:hover { background-color: #ea580c; }

.border { border-style: solid; border-width: 1px; }
.border-slate-200 { border-color: #e2e8f0; }
.border-slate-300 { border-color: #cbd5e1; }
.border-b { border-bottom-width: 1px; }

/* Typography */
.font-bold { font-weight: 700; }
.font-semibold { font-weight: 600; }
.font-medium { font-weight: 500; }
.text-2xl { font-size: 1.5rem; line-height: 2rem; }
.text-sm { font-size: 0.875rem; }
.text-xs { font-size: 0.75rem; }
.uppercase { text-transform: uppercase; }
.tracking-wider { letter-spacing: 0.05em; }
.font-mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace; }

/* Components */
.rounded-xl { border-radius: 0.75rem; }
.rounded-lg { border-radius: 0.5rem; }
.rounded-md { border-radius: 0.375rem; }
.rounded-full { border-radius: 9999px; }
.rounded { border-radius: 0.25rem; }
.shadow-custom { box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1); }
.shadow-sm { box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05); }
.overflow-hidden { overflow: hidden; }

/* Custom Buttons */
.gap-1 { gap: 0.25rem; }
.py-1\.5 { padding-top: 0.375rem; padding-bottom: 0.375rem; }
.hover\:bg-slate-100:hover { background-color: #f1f5f9; }

.btn-orange {
  background-color: #f97316;
  color: white;
  font-weight: 700;
  border-radius: 0.5rem;
  padding: 0.75rem 2.5rem;
  transition: all 0.2s;
  box-shadow: 0 10px 15px -3px rgba(249, 115, 22, 0.3);
}
.btn-orange:hover { background-color: #ea580c; transform: translateY(-1px); }
.btn-orange:active { transform: translateY(0); }

.btn-emerald {
  background-color: #10b981;
  padding: 0.5rem;
  border-radius: 0.5rem;
  transition: all 0.2s;
  box-shadow: 0 10px 15px -3px rgba(16, 185, 129, 0.2);
}
.btn-emerald:hover { background-color: #059669; }

.btn-icon-slate {
  padding: 0.5rem;
  border-radius: 0.5rem;
  transition: all 0.2s;
}
.btn-icon-slate:hover { background-color: #475569; }
.btn-icon-slate:hover .v-icon { color: white !important; }

/* Custom Select Override */
.custom-select { border-radius: 0.5rem; }
.custom-select ::v-deep .v-input__control { min-height: 48px !important; }
.custom-select ::v-deep .v-input__slot { 
  background-color: #f8fafc !important; 
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
  border-radius: 0.5rem !important;
  padding: 0 12px !important;
}
.custom-select ::v-deep .v-label { font-size: 0.875rem; color: #64748b; }

/* Custom Table Override */
.custom-table ::v-deep table { background-color: transparent !important; border-collapse: separate; border-spacing: 0; }
.custom-table ::v-deep thead { background-color: #f8fafc !important; }
.custom-table ::v-deep th { 
  color: #64748b !important; 
  font-weight: 700 !important; 
  text-transform: uppercase !important; 
  font-size: 0.75rem !important; 
  background-color: #f8fafc !important;
  border-bottom: 1px solid #e2e8f0 !important;
  height: 48px !important;
}
.custom-table ::v-deep td { 
  border-bottom: 1px solid #f1f5f9 !important;
  color: #334155 !important;
  font-size: 0.875rem !important;
}

/* Responsive */
@media (min-width: 768px) {
  .md\:flex-row { flex-direction: row; }
  .md\:w-auto { width: auto; }
}
</style>
